import { formatInteger } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { usePrefersReducedMotion } from '../../../ds/hooks';
import { LevelMeter } from '../../telephony/LevelMeter';
import { formatCallTimer, type CallSnapshot } from '../../telephony/callState';
import type { DialerState } from '../api';
import styles from './session.module.css';

export type BarPhase = 'idle' | 'dialing' | 'connected' | 'booked' | 'ended';

const PHASE_TEXT: Record<BarPhase, string> = {
  idle: 'bereit',
  dialing: 'wählt …',
  connected: 'verbunden',
  booked: 'Termin gebucht',
  ended: 'beendet',
};

/**
 * Queue-Leiste (Terminierung A2, 08–10): pulsing dot + timer, the list and „Kontakt 14 von 42 · verbunden",
 * the mode pill, „Als nächstes" and Stumm / Pause / Auflegen (Preview: „Anrufen" before the call). The level
 * meter of the running call (P-45) sits next to the timer.
 */
export function DialerQueueBar({
  state,
  call,
  phase,
  seconds,
  canDial,
  pausing,
  onDial,
  onMute,
  onPause,
  onHangup,
}: {
  state: DialerState;
  call: CallSnapshot;
  phase: BarPhase;
  seconds: number | null;
  canDial: boolean;
  pausing: boolean;
  onDial: () => void;
  onMute: () => void;
  onPause: () => void;
  onHangup: () => void;
}) {
  const reducedMotion = usePrefersReducedMotion();
  const live =
    phase === 'dialing' || phase === 'connected' || (phase === 'booked' && call.phase === 'connected');
  const timer = formatCallTimer((seconds ?? 0) * 1000);
  const position = `Kontakt ${formatInteger(state.progress.position)} von ${formatInteger(state.progress.total)}`;
  const next = state.nextUp ? `${state.nextUp.name} · ${state.nextUp.lifecycle.label}` : '—';
  const announcement =
    phase === 'connected'
      ? `verbunden · ${formatCallTimer(Math.floor((seconds ?? 0) / 10) * 10 * 1000)}`
      : PHASE_TEXT[phase];

  return (
    <div className={styles.queueBar} role="region" aria-label="Dialer-Queue">
      <div className={styles.barClock}>
        <span
          className={clsx(
            styles.barDot,
            live && !reducedMotion && styles.barDotPulse,
            !live && styles.barDotIdle,
          )}
          aria-hidden="true"
        />
        <span className={styles.barTimer}>{timer}</span>
        {live ? (
          <LevelMeter
            local={call.localStream}
            remote={call.remoteStream}
            active={call.phase === 'connected'}
            muted={call.muted}
          />
        ) : null}
      </div>
      <div className={styles.barSection}>
        <div className={styles.barEyebrow}>{state.listName ?? '—'}</div>
        <div className={styles.barState}>
          {position} · {PHASE_TEXT[phase]}
        </div>
      </div>
      <div className={styles.barPill}>{state.session?.dialModeLabel ?? ''}</div>
      <div className={clsx(styles.barSection, styles.barNext)}>
        <div className={styles.barEyebrow}>Als nächstes</div>
        <div className={styles.barNextName}>{next}</div>
      </div>
      <div className={styles.barActions}>
        <button
          type="button"
          className={styles.barGhost}
          aria-pressed={call.muted}
          disabled={call.phase !== 'connected'}
          onClick={onMute}
        >
          Stumm
        </button>
        <button type="button" className={styles.barGhost} disabled={live || pausing} onClick={onPause}>
          Pause
        </button>
        {canDial ? (
          <button type="button" className={styles.barDial} onClick={onDial}>
            Anrufen
          </button>
        ) : (
          <button type="button" className={styles.barHangup} disabled={!live} onClick={onHangup}>
            Auflegen
          </button>
        )}
      </div>
      <span className={styles.srOnly} aria-live="polite">
        {announcement}
      </span>
    </div>
  );
}
